import { z } from "zod/v4";

/**
 * TLD catalog create/edit validation schema.
 *
 * Money fields (registrarCost, registrationPrice, etc.) are in the smallest
 * currency unit per WiCloud convention — non-negative integers.
 */
export const tldCatalogSchema = z
  .object({
    tld: z
      .string()
      .min(2, "TLD is required")
      .max(20, "TLD must not exceed 20 characters")
      .regex(
        /^\.[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)*$/,
        'TLD must start with a dot followed by valid DNS labels, e.g. ".com" or ".co.uk"',
      ),
    displayName: z
      .string()
      .min(2, "Display name must be at least 2 characters")
      .max(100, "Display name must not exceed 100 characters"),
    registrarProvider: z
      .string()
      .min(2, "Registrar provider is required")
      .max(50, "Registrar provider must not exceed 50 characters"),
    registrarCost: z
      .number()
      .int("Registrar cost must be an integer")
      .min(0, "Registrar cost cannot be negative"),
    registrationPrice: z
      .number()
      .int("Registration price must be an integer")
      .min(0, "Registration price cannot be negative"),
    renewalPrice: z
      .number()
      .int("Renewal price must be an integer")
      .min(0, "Renewal price cannot be negative"),
    transferPrice: z
      .number()
      .int("Transfer price must be an integer")
      .min(0, "Transfer price cannot be negative"),
    restorePrice: z
      .number()
      .int("Restore price must be an integer")
      .min(0, "Restore price cannot be negative"),
    currency: z
      .string()
      .min(3, "Currency must be a 3-letter code")
      .max(3, "Currency must be a 3-letter code"),
    minRegistrationYears: z
      .number()
      .int("Minimum years must be an integer")
      .min(1, "Minimum years must be at least 1")
      .max(10, "Minimum years must not exceed 10"),
    maxRegistrationYears: z
      .number()
      .int("Maximum years must be an integer")
      .min(1, "Maximum years must be at least 1")
      .max(10, "Maximum years must not exceed 10"),
    supportsWhoisPrivacy: z.boolean(),
    whoisPrivacyPrice: z
      .number()
      .int("WHOIS privacy price must be an integer")
      .min(0, "WHOIS privacy price cannot be negative"),
    supportsDnsManagement: z.boolean(),
    supportsTransfer: z.boolean(),
    gracePeriodDays: z
      .number()
      .int("Grace period must be an integer")
      .min(0, "Grace period cannot be negative")
      .max(365, "Grace period must not exceed 365 days"),
    redemptionPeriodDays: z
      .number()
      .int("Redemption period must be an integer")
      .min(0, "Redemption period cannot be negative")
      .max(365, "Redemption period must not exceed 365 days"),
    isEnabled: z.boolean(),
    isFeatured: z.boolean(),
    sortOrder: z
      .number()
      .int("Sort order must be an integer")
      .min(0, "Sort order cannot be negative"),
  })
  .refine((data) => data.maxRegistrationYears >= data.minRegistrationYears, {
    message: "Maximum years must be greater than or equal to minimum years",
    path: ["maxRegistrationYears"],
  });

export type TldCatalogFormData = z.infer<typeof tldCatalogSchema>;
